import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  queryIsLoading,
  useGetFootprintingPorkManureAdditive,
  useGetFootprintingPorkPrimaryDataById,
  useGetManureAdditives,
  useKeyedFormList,
  useMutateFootprintingPorkManureAdditive,
  useMutateFootprintingPorkPrimaryData,
  useTranslation,
} from "utils";

import {
  ENUM_MANURE_ADDITIVE_UNITS,
  ENUM_PORK_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE,
  FARM_FOOTPRINTING_PORK_MANURE_ADDITIVE_ACTIVE,
  UploadableManureAdditive,
} from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Typography,
} from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const feedAdditivePlaceholder: UploadableManureAdditive = {
  id: undefined,
  manureAdditiveId: "",
  amountUsedM3: null,
  amountUsedM3Unit: null,
  enrichedSlurryPct: null,
};

const PorkManureAdditiveForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation("footprintingPork");
  const formRef = useRef<FormInstance>(null);

  const [manureAdditiveActive, setManureAdditiveActive] = useState<ENUM_PORK_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE | null>(null);

  const porkPrimaryDataFullQuery = useGetFootprintingPorkPrimaryDataById(primaryDataId ?? "");
  const { data: porkPrimaryData } = porkPrimaryDataFullQuery;
  const manureAdditivesUsageFullQuery = useGetFootprintingPorkManureAdditive(primaryDataId);
  const { data: manureAdditivesUsage } = manureAdditivesUsageFullQuery;
  const { data: porkManureAdditives } = useGetManureAdditives();
  const isLoading = [porkPrimaryDataFullQuery, manureAdditivesUsageFullQuery].some(queryIsLoading);
  const keyedManureAdditives = useKeyedFormList<UploadableManureAdditive>(formRef, { isReady: !isLoading });

  const selectGroupName = "footprinting-pork-manure-additive-select";

  const { mutate: mutatePorkManureAdditives } = useMutateFootprintingPorkManureAdditive(
    farmId,
    year,
    {
      primaryDataId: primaryDataId ?? "",
      selectedManureAdditive: keyedManureAdditives.payload.filter((m) => !!m.manureAdditiveId),
    },
    primaryDataId
  );

  const { mutate: mutatePorkPrimaryData } = useMutateFootprintingPorkPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      manureAdditiveActive,
    },
    primaryDataId
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutatePorkPrimaryData({});
        mutatePorkManureAdditives();
      }
    },
    [isLoading, mutatePorkPrimaryData, mutatePorkManureAdditives, readOnly]
  );

  // Load feed additives data
  useEffect(() => {
    if (manureAdditivesUsage) {
      keyedManureAdditives.actions.hydrate(
        manureAdditivesUsage.length
          ? manureAdditivesUsage.map((m) => ({
              id: m.id,
              manureAdditiveId: m.manureAdditiveId ?? "",
              amountUsedM3: m.amountUsedM3 ?? null,
              amountUsedM3Unit: m.amountUsedM3Unit ?? null,
              enrichedSlurryPct: m.enrichedSlurryPct ?? null,
            }))
          : [feedAdditivePlaceholder]
      );
    }
  }, [manureAdditivesUsage, keyedManureAdditives.actions]);

  // Load primary data
  useEffect(() => {
    if (porkPrimaryData) {
      setManureAdditiveActive(porkPrimaryData.manureAdditiveActive ?? null);

      formRef.current?.setFieldsValue({
        manureAdditiveActive: porkPrimaryData.manureAdditiveActive ?? null,
      });

      if (porkPrimaryData.manureAdditiveActive !== null && porkPrimaryData.manureAdditiveActive !== undefined) {
        keyedManureAdditives.actions.requestRehydrationValidation();
      }
    }
  }, [porkPrimaryData, keyedManureAdditives.actions]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-pork-manure-additives-form" className="padding-100">
      <Form ref={formRef} preserve={false}>
        <Flex vertical gap={16}>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <Typography variant="paragraph-m" style={{ maxWidth: "64%" }}>
              {t("porkManureAdditiveForm.manureAdditivesQuestion")}
              <span className="required-asterisk">*</span>
            </Typography>
            <KlimRadioGroup
              required
              name="manureAdditiveActive"
              dataTestId="footprinting-pork-manure-additives-radio"
              disabled={readOnly}
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setManureAdditiveActive(target.value);
                if (target.value === FARM_FOOTPRINTING_PORK_MANURE_ADDITIVE_ACTIVE.YES && keyedManureAdditives.entries.length === 0) {
                  keyedManureAdditives.actions.add(feedAdditivePlaceholder);
                }
              }}
              value={manureAdditiveActive}
              defaultValue={manureAdditiveActive}
              optionType="button"
              options={[FARM_FOOTPRINTING_PORK_MANURE_ADDITIVE_ACTIVE.YES, FARM_FOOTPRINTING_PORK_MANURE_ADDITIVE_ACTIVE.NO].map((manureAdditiveActive) => ({
                label: t(`porkManureAdditiveForm.manureAdditiveOptions.${manureAdditiveActive}`),
                value: manureAdditiveActive,
              }))}
            />
          </Flex>
          {manureAdditiveActive === FARM_FOOTPRINTING_PORK_MANURE_ADDITIVE_ACTIVE.YES &&
            keyedManureAdditives.entries.map((additive, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;

              return (
                <Form.Item key={additive.clientKey} className="padding-left-100 padding-right-100">
                  <Flex vertical gap={16}>
                    <Flex justify="space-between" align="center" vertical={false}>
                      <Typography variant="h5" style={{ flex: 1 }}>
                        {t("porkManureAdditiveForm.additiveNumber", { count: index + 1 })}
                      </Typography>
                      <IconButton
                        type="tertiary"
                        disabled={readOnly}
                        dataTestId={`delete-manure-additive-${index}`}
                        size="small"
                        iconName="tabler/trash"
                        onClick={() => {
                          keyedManureAdditives.actions.remove(additive.clientKey);
                          if (keyedManureAdditives.entries.length === 1) setManureAdditiveActive(null);
                        }}
                      />
                    </Flex>
                    <Select
                      disabled={readOnly}
                      name={`${selectGroupName}-${additive.clientKey}`}
                      dataTestId={selectGroupWithIndex}
                      label={t("porkManureAdditiveForm.question.manureAdditiveTypeQuestionInput")}
                      rules={[{ required: true, message: "" }]}
                      initialValue={porkManureAdditives?.find((f) => f.id === additive.manureAdditiveId)?.name || ""}
                      value={porkManureAdditives?.find((f) => f.id === additive.manureAdditiveId)?.name || ""}
                      onChange={(v) => {
                        const matchingPractice = porkManureAdditives?.find((f) => f.name === v);
                        keyedManureAdditives.actions.update(additive.clientKey, { manureAdditiveId: matchingPractice ? matchingPractice.id : "" });
                      }}
                      dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                    >
                      {(porkManureAdditives ?? []).map((porkManureAdditive) => {
                        // Check if this additive ID is already used in another item
                        const isAlreadyUsed = keyedManureAdditives.entries.some(
                          (a) => a.manureAdditiveId === porkManureAdditive.id && a.manureAdditiveId !== additive.manureAdditiveId
                        );

                        return (
                          <Option
                            disabled={readOnly || isAlreadyUsed}
                            key={`${selectGroupWithIndex}-${porkManureAdditive.id}`}
                            data-testid={`${selectGroupWithIndex}-${porkManureAdditive.id}`}
                            value={porkManureAdditive.name}
                          >
                            {porkManureAdditive.name}
                          </Option>
                        );
                      })}
                    </Select>

                    <InputNumberWithUnitSelector
                      key={`amount-${additive.clientKey}-${additive.manureAdditiveId}`}
                      className="floating-label"
                      disabled={readOnly}
                      dataTestId={`footprinting-pork-manure-additive-amount-input-${index}`}
                      name={`amountUsedM3-${additive.clientKey}`}
                      label={t("porkManureAdditiveForm.question.amountUsedQuestionInput")}
                      unitLabel={t("unit", { ns: "footprinting" })}
                      units={["kilograms", "liters"]}
                      unitDisplayOverride={{
                        kilograms: "kg / m³",
                        liters: "l / m³",
                      }}
                      value={additive.amountUsedM3}
                      initialValue={additive.amountUsedM3}
                      unitValue={additive.amountUsedM3Unit}
                      onChange={(v, unit) =>
                        keyedManureAdditives.actions.update(additive.clientKey, { amountUsedM3: v, amountUsedM3Unit: unit as ENUM_MANURE_ADDITIVE_UNITS })
                      }
                      validateFirst
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("porkManureAdditiveForm.error.amountUsedError")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />

                    <InputNumber
                      disabled={readOnly}
                      name={`enrichedSlurryPct-${additive.clientKey}`}
                      dataTestId="footprinting-manure-enriched-slurry-input"
                      label={t("porkManureAdditiveForm.question.enrichedSlurryQuestionInput")}
                      suffix="%"
                      value={formatFractionInput(additive.enrichedSlurryPct)}
                      initialValue={formatFractionInput(additive.enrichedSlurryPct)}
                      onChange={(v) => keyedManureAdditives.actions.update(additive.clientKey, { enrichedSlurryPct: formatPercentageInput(v) })}
                      validateFirst
                      rules={[
                        { required: true, message: t("porkManureAdditiveForm.error.validPercentage") },
                        { type: "number" },
                        {
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("porkManureAdditiveForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Flex>
                </Form.Item>
              );
            })}
          {manureAdditiveActive === FARM_FOOTPRINTING_PORK_MANURE_ADDITIVE_ACTIVE.YES && (
            <Button
              type="text"
              dataTestId="add-manure-additive-button"
              disabled={readOnly}
              onClick={() => {
                keyedManureAdditives.actions.add(feedAdditivePlaceholder);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("porkManureAdditiveForm.add")}
            </Button>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

PorkManureAdditiveForm.Key = "pork.manureAdditive";
export { PorkManureAdditiveForm };
